import { useMemo, useState, type ReactNode } from 'react';
import { Combobox } from '@base-ui/react/combobox';
import { DEFAULT_LABELS, type SearchSelectLabels } from './search-select-labels.ts';
import { SearchSelectInputGroup } from './search-select-input-group.tsx';
import { SearchSelectPopup } from './search-select-popup.tsx';
import { SEARCH_DEBOUNCE_MS, useAsyncOptions } from './useAsyncOptions.ts';

interface SearchSelectBaseProps<T> {
  /**
   * Fetches the options for a query. Called with an abort signal that is
   * tripped as soon as the answer stops being the one the field is waiting for.
   */
  loadOptions: (query: string, signal: AbortSignal) => Promise<T[]>;
  /** What makes two items the same item — an id, usually. */
  getKey: (item: T) => string;
  /** The item as one line of text: what the field shows once it is picked. */
  getLabel: (item: T) => string;
  /** A richer row for the list. The label is still what the field itself shows. */
  renderOption?: (item: T) => ReactNode;
  placeholder?: string;
  /** Put on the text field, so a `<label htmlFor>` names it. */
  id?: string;
  /** Names the field where no visible label does. */
  'aria-label'?: string;
  /** Points the text field at the lines that explain it, such as a `Field`'s error. */
  'aria-describedby'?: string;
  disabled?: boolean;
  /** Offers an × that empties the field. */
  clearable?: boolean;
  invalid?: boolean;
  /**
   * How much has to be typed before anything is searched. Zero — the default —
   * means opening the field asks for the first page of results, which is what
   * makes it usable by someone who does not yet know what to type.
   */
  minQueryLength?: number;
  debounceMs?: number;
  labels?: Partial<SearchSelectLabels>;
  className?: string;
}

export interface SearchSelectSingleProps<T> extends SearchSelectBaseProps<T> {
  multiple?: false;
  value: NoInfer<T> | null;
  onChange: (value: NoInfer<T> | null) => void;
}

export interface SearchSelectMultipleProps<T> extends SearchSelectBaseProps<T> {
  multiple: true;
  value: NoInfer<T>[];
  onChange: (value: NoInfer<T>[]) => void;
}

/*
 * The item type is read off `loadOptions`, `getKey` and `getLabel` alone —
 * `NoInfer` keeps `value` and `onChange` out of it. Both would otherwise pull
 * the wrong way: a `useState` setter also accepts an updater function, and the
 * two members of this union disagree about whether the value is one item or
 * several, so inferring from it produced `Person[]` where `Person` was meant.
 */
export type SearchSelectProps<T> =
  | SearchSelectSingleProps<T>
  | SearchSelectMultipleProps<T>;

/**
 * A field whose options live on the server: type, and the list is fetched.
 *
 * The counterpart of `Combobox`, which holds every candidate up front. Here
 * there is no such list — the users, organisations and cohorts a staff member
 * picks between run into five figures — so the typing is a query, the answer
 * arrives late, and the field has to say so: it debounces, it abandons the
 * results it no longer wants, and it shows loading, empty and failed inside
 * the popup rather than as an empty list that reads as "none".
 *
 * Free text is not an answer here, which is the other half of the difference:
 * what comes back is a record, and half a name is not one.
 *
 * Single by default; `multiple` turns the picks into chips.
 */
export function SearchSelect<T>(props: SearchSelectProps<T>) {
  if (props.multiple === true) {
    const { multiple: _multiple, ...rest } = props;
    return <SearchSelectField {...rest} multiple />;
  }

  const { multiple: _multiple, value, onChange, ...rest } = props;
  return (
    <SearchSelectField
      {...rest}
      multiple={false}
      value={value === null ? [] : [value]}
      onChange={(next) => onChange(next[0] ?? null)}
    />
  );
}

interface SearchSelectFieldProps<T> extends SearchSelectBaseProps<T> {
  multiple: boolean;
  /** Both modes normalised to a list, so one component draws both. */
  value: T[];
  onChange: (value: T[]) => void;
}

function SearchSelectField<T>({
  loadOptions,
  getKey,
  getLabel,
  renderOption,
  placeholder,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  disabled = false,
  clearable = false,
  invalid = false,
  minQueryLength = 0,
  debounceMs = SEARCH_DEBOUNCE_MS,
  labels,
  className,
  multiple,
  value,
  onChange,
}: SearchSelectFieldProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const text = { ...DEFAULT_LABELS, ...labels };

  const { status, options } = useAsyncOptions({
    loadOptions,
    query,
    enabled: open,
    minQueryLength,
    debounceMs,
  });

  /*
   * Whatever is picked stays in the list even when the newest results have
   * never heard of it: Base UI reads a selected item's label out of `items`, so
   * dropping it would blank the field the moment someone searches for something
   * else.
   */
  const items = useMemo(() => {
    const known = new Set(options.map(getKey));
    return [...options, ...value.filter((item) => !known.has(getKey(item)))];
  }, [options, value, getKey]);

  function handleValueChange(next: T[] | T | null): void {
    /*
     * Base UI hands back an array in multiple mode and a single value in single
     * mode. `multiple` says which; the casts state that rather than guess from
     * the shape, because an item type may itself be an array.
     */
    if (multiple) {
      onChange(next as T[]);
      return;
    }
    onChange(next === null ? [] : [next as T]);
  }

  return (
    <Combobox.Root<T, boolean>
      items={items}
      multiple={multiple}
      value={multiple ? value : (value[0] ?? null)}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={(next) => setOpen(next)}
      /* The server did the filtering; filtering the answer again would hide rows it deliberately returned. */
      filter={null}
      disabled={disabled}
      itemToStringLabel={getLabel}
      isItemEqualToValue={(a, b) => getKey(a) === getKey(b)}
      onInputValueChange={(next, { reason }) => {
        /*
         * Picking an item in multiple mode empties the input, which would
         * otherwise read as "the user cleared the query" and throw away the
         * results they are still picking from.
         */
        if (reason !== 'item-press') {
          setQuery(next);
        }
      }}
    >
      <SearchSelectInputGroup
        getKey={getKey}
        getLabel={getLabel}
        placeholder={placeholder}
        id={id}
        ariaLabel={ariaLabel}
        ariaDescribedBy={ariaDescribedBy}
        disabled={disabled}
        clearable={clearable}
        invalid={invalid}
        multiple={multiple}
        hasValue={value.length > 0}
        labels={text}
        className={className}
      />
      <SearchSelectPopup
        status={status}
        queryTooShort={query.length < minQueryLength}
        getKey={getKey}
        getLabel={getLabel}
        renderOption={renderOption}
        labels={text}
      />
    </Combobox.Root>
  );
}
